import React, { useState, useEffect, useCallback } from 'react';
import Itinerary from './Itinerary';
import { useAuth } from './AuthContext';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import RequestDocument from './RequestDocument';
import { API_URL } from './authService';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

function Requests({ onGenerateBudget }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [searchField, setSearchField] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [itinerary, setItinerary] = useState([]);
  const pageSize = 10;
  const { token } = useAuth();

  const fetchRequests = useCallback(async (page = 0, sort = 'id,desc', search = '') => {
    if (!token) return;
    setLoading(true);
    try {
      const sortParam = sort ? `&sort=${sort}` : '';
      
      // Lógica para manejar filtros complejos que el backend no soporta nativamente en /requests
      let searchParam = search ? `&${search}` : '';
      
      if (search) {
        if (search.includes('passenger.name.contains') || search.includes('passenger.surname.contains')) {
          const isName = search.includes('passenger.name.contains');
          const value = search.split('=')[1];
          const passengerField = isName ? 'name.contains' : 'surname.contains';
          
          // 1. Buscar pasajeros
          const passResponse = await fetch(`${API_URL}/api/passengers?${passengerField}=${value}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (passResponse.ok) {
            const passengers = await passResponse.json();
            const passengerIds = passengers.map(p => p.id);
            
            if (passengerIds.length > 0) {
              searchParam = `&passengerId.in=${passengerIds.join(',')}`;
            } else {
              setRequests([]);
              setTotalElements(0);
              setTotalPages(0);
              setLoading(false);
              return;
            }
          }
        } else if (search.includes('requestDestinations.destination.destinationName.contains')) {
           const value = search.split('=')[1];
           
           // 1. Buscar destinos
           const destResponse = await fetch(`${API_URL}/api/destinations?destinationName.contains=${value}`, {
             headers: { 'Authorization': `Bearer ${token}` }
           });
           
           if (destResponse.ok) {
             const destinations = await destResponse.json();
             const destIds = destinations.map(d => d.id);
             
             if (destIds.length > 0) {
               // 2. Buscar request-destinations con esos destinos
               const reqDestResponse = await fetch(`${API_URL}/api/request-destinations?destinationId.in=${destIds.join(',')}`, {
                 headers: { 'Authorization': `Bearer ${token}` }
               });
               
               if (reqDestResponse.ok) {
                 const reqDestinations = await reqDestResponse.json();
                 const requestIds = [...new Set(reqDestinations.map(rd => rd.request?.id || rd.requestId).filter(id => id))];
                 
                 if (requestIds.length > 0) {
                   searchParam = `&id.in=${requestIds.join(',')}`;
                 } else {
                   setRequests([]);
                   setTotalElements(0);
                   setTotalPages(0);
                   setLoading(false);
                   return;
                 }
               }
             } else {
               setRequests([]);
               setTotalElements(0);
               setTotalPages(0);
               setLoading(false);
               return;
             }
           }
        } else if (search.includes('requestDestinations.destination.country.contains')) {
           const value = search.split('=')[1];
           
           // 1. Buscar destinos por país
           const destResponse = await fetch(`${API_URL}/api/destinations?country.contains=${value}`, {
             headers: { 'Authorization': `Bearer ${token}` }
           });
           
           if (destResponse.ok) {
             const destinations = await destResponse.json();
             const destIds = destinations.map(d => d.id);
             
             if (destIds.length > 0) {
               // 2. Buscar request-destinations con esos destinos
               const reqDestResponse = await fetch(`${API_URL}/api/request-destinations?destinationId.in=${destIds.join(',')}`, {
                 headers: { 'Authorization': `Bearer ${token}` }
               });
               
               if (reqDestResponse.ok) {
                 const reqDestinations = await reqDestResponse.json();
                 const requestIds = [...new Set(reqDestinations.map(rd => rd.request?.id || rd.requestId).filter(id => id))];
                 
                 if (requestIds.length > 0) {
                   searchParam = `&id.in=${requestIds.join(',')}`;
                 } else {
                   setRequests([]);
                   setTotalElements(0);
                   setTotalPages(0);
                   setLoading(false);
                   return;
                 }
               }
             } else {
               setRequests([]);
               setTotalElements(0);
               setTotalPages(0);
               setLoading(false);
               return;
             }
           }
        }
      }

      const response = await fetch(`${API_URL}/api/requests?page=${page}&size=${pageSize}${sortParam}${searchParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      let data = await response.json();
      
      // Obtener datos completos de pasajeros y destinos para cada solicitud
      data = await Promise.all(
        data.map(async (request) => {
          let passengerData = null;
          if (request.passenger && request.passenger.id) {
            try {
              const passengerResponse = await fetch(`${API_URL}/api/passengers/${request.passenger.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (passengerResponse.ok) passengerData = await passengerResponse.json();
            } catch (error) { console.error('Error fetching passenger data:', error); }
          }
          
          let requestDestinations = [];
          if (request.id) {
            try {
              const destinationsResponse = await fetch(`${API_URL}/api/request-destinations?requestId.equals=${request.id}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
              });
              if (destinationsResponse.ok) {
                  const destinations = await destinationsResponse.json();
                  requestDestinations = await Promise.all(destinations.map(async rd => {
                      if (rd.destination && rd.destination.id) {
                          try {
                              const destDetailsResponse = await fetch(`${API_URL}/api/destinations/${rd.destination.id}`, {
                                  headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (destDetailsResponse.ok) {
                                  const destinationDetails = await destDetailsResponse.json();
                                  return { ...rd, destination: destinationDetails };
                              }
                          } catch (e) {
                              console.error("Failed to fetch destination details", e);
                              return { ...rd, destination: { ...rd.destination, destinationName: 'Error al cargar' } };
                          }
                      }
                      return { ...rd, destination: { ...rd.destination, destinationName: 'N/A' } };
                  }));
              }
            } catch (error) { console.error('Error fetching request destinations:', error); }
          }

          return { ...request, passenger: passengerData, requestDestinations };
        })
      );
      
      const totalCount = parseInt(response.headers.get('X-Total-Count') || '0');
      const totalPages = Math.ceil(totalCount / pageSize);
      
      setRequests(data);
      setTotalPages(totalPages);
      setTotalElements(totalCount);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRequests(0, sortField ? `${sortField},${sortDirection}` : '');
  }, [fetchRequests, sortField, sortDirection]);

  const handlePageChange = (newPage) => {
    setLoading(true);
    const sortParam = sortField ? `${sortField},${sortDirection}` : '';
    const searchParam = buildSearchParam();
    fetchRequests(newPage, sortParam, searchParam);
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    setLoading(true);
    const searchParam = buildSearchParam();
    fetchRequests(0, `${field},${newDirection}`, searchParam);
  };

  const getSortArrow = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const buildSearchParam = () => {
    if (!searchField || !searchValue.trim()) return '';
    
    const fieldMap = {
      'id': 'id.equals',
      'passengerName': 'passenger.name.contains',
      'passengerSurname': 'passenger.surname.contains',
      'destination': 'requestDestinations.destination.destinationName.contains',
      'country': 'requestDestinations.destination.country.contains'
    };
    
    const apiField = fieldMap[searchField];
    return apiField ? `${apiField}=${encodeURIComponent(searchValue.trim())}` : '';
  };

  const handleSearchFieldChange = (e) => {
    setSearchField(e.target.value);
    setSearchValue('');
    setCurrentPage(0);
    if (!e.target.value) {
      setLoading(true);
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      fetchRequests(0, sortParam);
    }
  };

  const handleSearchValueChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(0);
  };

  useEffect(() => {
    if (searchField && searchValue.trim()) {
      const timeoutId = setTimeout(() => {
        setLoading(true);
        const sortParam = sortField ? `${sortField},${sortDirection}` : '';
        const searchParam = buildSearchParam();
        fetchRequests(0, sortParam, searchParam);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else if (searchField && !searchValue.trim()) {
      setLoading(true);
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      fetchRequests(0, sortParam);
    }
  }, [searchValue, searchField, sortField, sortDirection]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [passengerData, setPassengerData] = useState({
    name: '',
    surname: '',
    dni: '',
    phoneNumber: '',
    email: ''
  });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [passengerStatus, setPassengerStatus] = useState(''); // 'existing', 'new', 'conflict', ''
  const [conflictPassenger, setConflictPassenger] = useState(null);
  const [currentSearchField, setCurrentSearchField] = useState('');
  const [formData, setFormData] = useState({
    origin: '',
    numberPassengers: '',
    numberChilds: '',
    numberInfants: '',
    luggage: '',
    accommodationPreference: '',
    category: '',
    mealPlan: '',
    roomType: '',
    ubicacion: '',
    transfer: false,
    carRental: false,
    excursion: false,
    traveInsurance: false,
    budget: '',
    annotations: '',
    specialNeeds: '',
    comments: '',
    passenger: { id: null }
  });

  const searchPassengers = async (field, value) => {
    if (!value || value.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      let searchParam = '';
      
      if (field === 'dni') {
        searchParam = `dni.equals=${encodeURIComponent(value)}`;
      } else {
        const fieldMap = {
          'name': 'name.contains',
          'surname': 'surname.contains',
          'phoneNumber': 'phoneNumber.contains',
          'email': 'email.contains'
        };
        searchParam = `${fieldMap[field]}=${encodeURIComponent(value)}`;
      }

      const response = await fetch(`${API_URL}/api/passengers?${searchParam}&size=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      }
    } catch (error) {
      console.error('Error searching passengers:', error);
    }
  };

  const checkExistingPassenger = async () => {
    const { name, surname, dni } = passengerData;
    if (!name || !name.trim() || !surname || !surname.trim() || !dni || String(dni).trim() === '') {
      setPassengerStatus('');
      setSelectedPassenger(null);
      setConflictPassenger(null);
      return;
    }

    try {
      const dniResponse = await fetch(`${API_URL}/api/passengers?dni.equals=${encodeURIComponent(String(dni))}&size=1`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (dniResponse.ok) {
        const dniData = await dniResponse.json();
        if (dniData.length > 0) {
          const existingPassenger = dniData[0];
          
          const nameMatch = existingPassenger.name?.toLowerCase().trim() === name.toLowerCase().trim();
          const surnameMatch = existingPassenger.surname?.toLowerCase().trim() === surname.toLowerCase().trim();
          
          if (nameMatch && surnameMatch) {
            setSelectedPassenger(existingPassenger);
            setPassengerStatus('existing');
            setConflictPassenger(null);
            setFormData(prev => ({ ...prev, passenger: { id: existingPassenger.id } }));
          } else {
            setSelectedPassenger(null);
            setConflictPassenger(existingPassenger);
            setPassengerStatus('conflict');
            setFormData(prev => ({ ...prev, passenger: { id: null } }));
          }
          return;
        }
      }
      
      setSelectedPassenger(null);
      setConflictPassenger(null);
      setPassengerStatus('new');
      setFormData(prev => ({ ...prev, passenger: { id: null } }));
      
    } catch (error) {
      console.error('Error checking passenger:', error);
    }
  };

  const createPassengerAPI = async (passengerData) => {
    const cleanedData = {
      ...passengerData,
      sex: null
    };
    const response = await fetch(`${API_URL}/api/passengers`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(cleanedData)
    });
    return response.json();
  };

  const updatePassengerAPI = async (id, passengerData) => {
    const cleanedData = {
      ...passengerData,
      sex: passengerData.sex || null
    };
    const response = await fetch(`${API_URL}/api/passengers/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ...cleanedData, id })
    });
    return response.json();
  };

  const handlePassengerInputChange = (e) => {
    const { name, value } = e.target;
    setPassengerData(prev => ({ ...prev, [name]: value }));
    setCurrentSearchField(name);
    
    if (name === 'name' || name === 'surname' || name === 'dni') {
      setPassengerStatus('');
      setSelectedPassenger(null);
      setConflictPassenger(null);
      setFormData(prev => ({ ...prev, passenger: { id: null } }));
    }
    
    if (value.length >= 2) {
      searchPassengers(name, value);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handlePassengerInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const handlePassengerInputFocus = (fieldName) => {
    setCurrentSearchField(fieldName);
    if (passengerData[fieldName] && passengerData[fieldName].length >= 2 && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const selectSuggestion = (passenger) => {
    setPassengerData({
      name: passenger.name || '',
      surname: passenger.surname || '',
      dni: String(passenger.dni || ''),
      phoneNumber: passenger.phoneNumber || '',
      email: passenger.email || ''
    });
    setSelectedPassenger(passenger);
    setPassengerStatus('existing');
    setFormData(prev => ({ ...prev, passenger: { id: passenger.id } }));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkExistingPassenger();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [passengerData.name, passengerData.surname, passengerData.dni]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (name === 'numberPassengers' || name === 'numberChilds' || name === 'numberInfants' || name === 'budget') {
      setFormData({ ...formData, [name]: parseInt(value) || 0 });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const createRequest = async (requestData) => {
    const response = await fetch(`${API_URL}/api/requests`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    });
    if (!response.ok) throw new Error('Failed to create request');
    return response.json();
  };

  const updateRequest = async (id, requestData) => {
    const response = await fetch(`${API_URL}/api/requests/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ...requestData, id })
    });
    if (!response.ok) throw new Error('Failed to update request');
    return response.json();
  };

  const deleteRequest = async (id) => {
    await fetch(`${API_URL}/api/requests/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
  };

  const saveRequestDestinations = async (requestId, currentDestinations) => {
    
    // 1. Obtener destinos existentes en BD
    const response = await fetch(`${API_URL}/api/request-destinations?requestId.equals=${requestId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    let existingDestinations = [];
    if (response.ok) existingDestinations = await response.json();

    const processedIds = [];

    // 2. Actualizar o Crear
    for (const dest of currentDestinations) {
        const payload = {
            departureDate: dest.departureDate || null,
            returnDate: dest.returnDate || null,
            numberNights: dest.numberNights,
            request: { id: requestId },
            destination: { id: dest.destinationId }
        };

        // Si tiene ID y existe en la BD, actualizamos (PUT)
        if (dest.id && existingDestinations.some(e => String(e.id) === String(dest.id))) {
            await fetch(`${API_URL}/api/request-destinations/${dest.id}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...payload, id: dest.id })
            });
            processedIds.push(dest.id);
        } else {
            // Si no tiene ID (o es temporal), creamos (POST)
            await fetch(`${API_URL}/api/request-destinations`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
        }
    }

    // 3. Eliminar los que ya no están en la lista
    for (const existing of existingDestinations) {
        if (!processedIds.includes(existing.id)) {
            await fetch(`${API_URL}/api/request-destinations/${existing.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
        }
    }
  };



  const deleteRequestDestinations = async (requestId) => {
      // First, fetch the destinations to get their specific IDs
      const response = await fetch(`${API_URL}/api/request-destinations?requestId.equals=${requestId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
          const destinations = await response.json();
          // Then, delete each one
          for (const dest of destinations) {
              await fetch(`${API_URL}/api/request-destinations/${dest.id}`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${token}` }
              });
          }
      }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (passengerStatus === 'conflict') {
      alert('No se puede crear la solicitud. Existe un conflicto con el DNI ingresado.');
      return;
    }
    
    try {
      let passengerId = formData.passenger.id;
      
      if (passengerId && selectedPassenger && passengerStatus === 'existing') {
        const phoneChanged = (passengerData.phoneNumber || '') !== (selectedPassenger.phoneNumber || '');
        const emailChanged = (passengerData.email || '') !== (selectedPassenger.email || '');
        
        if (phoneChanged || emailChanged) {
          try {
            await updatePassengerAPI(passengerId, {
              ...selectedPassenger,
              phoneNumber: passengerData.phoneNumber,
              email: passengerData.email
            });
          } catch (error) {
            console.error('Error updating passenger:', error);
            alert('Error al actualizar los datos del pasajero.');
            return;
          }
        }
      }
      
      if (!passengerId && passengerStatus === 'new') {
        try {
          const newPassenger = await createPassengerAPI(passengerData);
          passengerId = newPassenger.id;
        } catch (error) {
          console.error('Error creating passenger:', error);
          alert('Error: No se pudo crear el pasajero. Verifique que los datos sean correctos.');
          return;
        }
      }
      
      const requestData = { ...formData, passenger: { id: passengerId } };
      const enumFields = ['accommodationPreference', 'category', 'mealPlan', 'roomType'];
      enumFields.forEach(field => {
        if (requestData[field] === '') requestData[field] = null;
      });
      
      if (editingId) {
        await updateRequest(editingId, requestData);
        await saveRequestDestinations(editingId, itinerary);
      } else {
        const newRequest = await createRequest(requestData);
        await saveRequestDestinations(newRequest.id, itinerary);
      }
      resetForm();
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      const searchParam = buildSearchParam();
      fetchRequests(currentPage, sortParam, searchParam);
    } catch (error) {
      console.error('Error saving request:', error);
      alert('Error al guardar la solicitud. Por favor, intente nuevamente.');
    }
  };

  const handleEdit = (request) => {
    setFormData(request);
    setEditingId(request.id);
    setShowForm(true);
    
    if (request.passenger && request.passenger.id) {
      fetchPassengerById(request.passenger.id);
    }
    
    if (request.requestDestinations && Array.isArray(request.requestDestinations)) {
        const adaptedItinerary = request.requestDestinations.map(rd => ({
            id: rd.id,
            destinationId: rd.destination ? rd.destination.id : null,
            destinationName: rd.destination ? `${rd.destination.destinationName} - ${rd.destination.country}` : "Desconocido",
            departureDate: rd.departureDate,
            returnDate: rd.returnDate,
            numberNights: rd.numberNights
        }));
        setItinerary(adaptedItinerary);
    } else {
        setItinerary([]);
    }
  };

  const fetchPassengerById = async (passengerId) => {
    try {
      const response = await fetch(`${API_URL}/api/passengers/${passengerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const passenger = await response.json();
        setPassengerData({
          name: passenger.name || '',
          surname: passenger.surname || '',
          dni: String(passenger.dni || ''),
          phoneNumber: passenger.phoneNumber || '',
          email: passenger.email || ''
        });
        setSelectedPassenger(passenger);
        setPassengerStatus('existing');
      }
    } catch (error) {
      console.error('Error fetching passenger:', error);
    }
  };

  const handleView = (request) => {
    setViewingRequest(request);
    setShowViewModal(true);
  };

  const handleDelete = (id) => {
    setRequestToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteRequestDestinations(requestToDelete);
      await deleteRequest(requestToDelete);
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      const searchParam = buildSearchParam();
      fetchRequests(currentPage, sortParam, searchParam);
    } catch (error) {
      console.error('Error deleting request:', error);
    }
    setShowDeleteModal(false);
    setRequestToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setRequestToDelete(null);
  };

  const resetForm = () => {
    setFormData({
      origin: '', numberPassengers: '', numberChilds: '', numberInfants: '', luggage: '',
      accommodationPreference: '', category: '', mealPlan: '', roomType: '', ubicacion: '',
      transfer: false, carRental: false, excursion: false, traveInsurance: false,
      budget: '', annotations: '', specialNeeds: '', comments: '', passenger: { id: null }
    });
    setPassengerData({
      name: '', surname: '', dni: '', phoneNumber: '', email: ''
    });
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedPassenger(null);
    setConflictPassenger(null);
    setPassengerStatus('');
    setItinerary([]);
    setShowForm(false);
    setEditingId(null);
  };

  const handleGenerateBudgetClick = (request) => {
    if (onGenerateBudget) {
      const adaptedItinerary = request.requestDestinations ? request.requestDestinations.map((rd, index) => ({
        id: Date.now() + index, // ID temporal para la UI
        destinationId: rd.destination ? rd.destination.id : null,
        destinationName: rd.destination ? `${rd.destination.destinationName} - ${rd.destination.country}` : "Desconocido",
        departureDate: rd.departureDate,
        returnDate: rd.returnDate,
        numberNights: rd.numberNights
      })) : [];

      onGenerateBudget({
        requestId: request.id,
        passenger: request.passenger,
        itinerary: adaptedItinerary
      });
    }
  };

  const accommodationMap = {
    HOTEL: 'Hotel', CABANA: 'Cabaña', APART: 'Apartamento',
    RESORT: 'Resort', HOSTE: 'Hostel', '': 'No especificado'
  };
  const categoryMap = {
    ESTANDAR: 'Estándar', SUPERIOR: 'Superior', LUJO: 'Lujo',
    INDIFERENTE: 'Indiferente', '': 'No especificado'
  };
  const mealPlanMap = {
    SIN_PENSION: 'Sin Pensión', DESAYUNO: 'Desayuno', MEDIA_PENSION: 'Media Pensión',
    ALL_INCLUSIVE: 'Todo Incluido', '': 'No especificado'
  };
  const roomTypeMap = {
    SIMPLE: 'Simple', DOBLE: 'Doble', TRIPLE: 'Triple',
    SUITE_FAMILIAR: 'Suite Familiar', '': 'No especificado'
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Gestión de Solicitudes</h2>
        <button onClick={() => setShowForm(true)} style={styles.addButton}>
          + Agregar Solicitud
        </button>
      </div>

      <div style={styles.searchContainer}>
        <div style={styles.searchField}>
          <label style={styles.searchLabel}>Buscar por:</label>
          <select 
            value={searchField} 
            onChange={handleSearchFieldChange} 
            style={styles.searchSelect}
          >
            <option value="">Seleccionar campo</option>
            <option value="id">ID</option>
            <option value="passengerName">Nombre del Pasajero</option>
            <option value="passengerSurname">Apellido del Pasajero</option>
            <option value="destination">Destino</option>
            <option value="country">País de Destino</option>
          </select>
        </div>
        {searchField && (
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>Valor:</label>
            <input
              type="text"
              value={searchValue}
              onChange={handleSearchValueChange}
              placeholder={`Buscar por ${searchField === 'id' ? 'ID' : searchField === 'passengerName' ? 'nombre del pasajero' : searchField === 'passengerSurname' ? 'apellido del pasajero' : searchField === 'destination' ? 'destino' : 'país'}...`}
              style={styles.searchInput}
            />
          </div>
        )}
        {(searchField || searchValue) && (
          <button 
            onClick={() => {
              setSearchField('');
              setSearchValue('');
              setCurrentPage(0);
              setLoading(true);
              const sortParam = sortField ? `${sortField},${sortDirection}` : '';
              fetchRequests(0, sortParam);
            }}
            style={styles.clearButton}
          >
            Limpiar
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={styles.form}>
          <h3 style={styles.formTitle}>{editingId ? 'Editar Solicitud' : 'Nueva Solicitud'}</h3>
          
          <div style={styles.passengerSection}>
            <h4 style={styles.sectionTitle}>Datos del Pasajero</h4>
            {passengerStatus === 'existing' && <div style={styles.statusMessage}>✅ Pasajero existente: {selectedPassenger?.name} {selectedPassenger?.surname}</div>}
            {passengerStatus === 'new' && <div style={styles.statusMessageNew}>➕ Se creará un nuevo pasajero.</div>}
            {passengerStatus === 'conflict' && <div style={styles.statusMessageError}>⚠️ CONFLICTO: El DNI ya pertenece a {conflictPassenger?.name} {conflictPassenger?.surname}.</div>}
            
            <div style={styles.passengerGrid}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Apellido *</label>
                <div style={styles.autocompleteContainer}>
                  <input name="surname" value={passengerData.surname} onChange={handlePassengerInputChange} onBlur={handlePassengerInputBlur} onFocus={() => handlePassengerInputFocus('surname')} style={styles.input} required autoComplete="off" />
                   {showSuggestions && suggestions.length > 0 && currentSearchField === 'surname' && (
                    <div style={styles.suggestionsDropdown}>
                      {suggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => selectSuggestion(p)}>{p.surname} {p.name} - DNI: {p.dni}</div>)}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Nombre *</label>
                <div style={styles.autocompleteContainer}>
                  <input name="name" value={passengerData.name} onChange={handlePassengerInputChange} onBlur={handlePassengerInputBlur} onFocus={() => handlePassengerInputFocus('name')} style={styles.input} required autoComplete="off" />
                  {showSuggestions && suggestions.length > 0 && currentSearchField === 'name' && (
                    <div style={styles.suggestionsDropdown}>
                      {suggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => selectSuggestion(p)}>{p.surname} {p.name} - DNI: {p.dni}</div>)}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>DNI *</label>
                <div style={styles.autocompleteContainer}>
                  <input name="dni" value={passengerData.dni} onChange={handlePassengerInputChange} onBlur={handlePassengerInputBlur} onFocus={() => handlePassengerInputFocus('dni')} style={styles.input} required autoComplete="off" />
                   {showSuggestions && suggestions.length > 0 && currentSearchField === 'dni' && (
                    <div style={styles.suggestionsDropdown}>
                      {suggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => selectSuggestion(p)}>{p.surname} {p.name} - DNI: {p.dni}</div>)}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Teléfono</label>
                <div style={styles.autocompleteContainer}>
                  <input name="phoneNumber" value={passengerData.phoneNumber} onChange={handlePassengerInputChange} onBlur={handlePassengerInputBlur} onFocus={() => handlePassengerInputFocus('phoneNumber')} style={styles.input} autoComplete="off" />
                   {showSuggestions && suggestions.length > 0 && currentSearchField === 'phoneNumber' && (
                    <div style={styles.suggestionsDropdown}>
                      {suggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => selectSuggestion(p)}>{p.surname} {p.name} - DNI: {p.dni}</div>)}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Email</label>
                <div style={styles.autocompleteContainer}>
                  <input name="email" type="email" value={passengerData.email} onChange={handlePassengerInputChange} onBlur={handlePassengerInputBlur} onFocus={() => handlePassengerInputFocus('email')} style={styles.input} autoComplete="off" />
                  {showSuggestions && suggestions.length > 0 && currentSearchField === 'email' && (
                    <div style={styles.suggestionsDropdown}>
                      {suggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => selectSuggestion(p)}>{p.surname} {p.name} - DNI: {p.dni}</div>)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Itinerary itinerary={itinerary} setItinerary={setItinerary} />
          
          <div style={styles.requestSection}>
            <h4 style={styles.sectionTitle}>Datos de la Solicitud</h4>
            <div style={styles.formGrid}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Origen</label>
                <input name="origin" value={formData.origin} onChange={handleInputChange} style={styles.input} />
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Número de Pasajeros</label>
                <input name="numberPassengers" type="number" value={formData.numberPassengers} onChange={handleInputChange} style={styles.input} />
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Número de Niños</label>
                <input name="numberChilds" type="number" value={formData.numberChilds} onChange={handleInputChange} style={styles.input} />
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Número de Bebés</label>
                <input name="numberInfants" type="number" value={formData.numberInfants} onChange={handleInputChange} style={styles.input} />
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Equipaje</label>
                <input name="luggage" value={formData.luggage} onChange={handleInputChange} style={styles.input} />
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Preferencia de Alojamiento</label>
                <select name="accommodationPreference" value={formData.accommodationPreference} onChange={handleInputChange} style={styles.select}>
                  <option value="">Seleccionar</option>
                  <option value="HOTEL">Hotel</option>
                  <option value="CABANA">Cabaña</option>
                  <option value="APART">Apartamento</option>
                  <option value="RESORT">Resort</option>
                  <option value="HOSTE">Hostel</option>
                </select>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Categoría</label>
                <select name="category" value={formData.category} onChange={handleInputChange} style={styles.select}>
                  <option value="">Seleccionar</option>
                  <option value="ESTANDAR">Estándar</option>
                  <option value="SUPERIOR">Superior</option>
                  <option value="LUJO">Lujo</option>
                  <option value="INDIFERENTE">Indiferente</option>
                </select>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Plan de Comidas</label>
                <select name="mealPlan" value={formData.mealPlan} onChange={handleInputChange} style={styles.select}>
                  <option value="">Seleccionar</option>
                  <option value="SIN_PENSION">Sin Pensión</option>
                  <option value="DESAYUNO">Desayuno</option>
                  <option value="MEDIA_PENSION">Media Pensión</option>
                  <option value="ALL_INCLUSIVE">Todo Incluido</option>
                </select>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Tipo de Habitación</label>
                <select name="roomType" value={formData.roomType} onChange={handleInputChange} style={styles.select}>
                  <option value="">Seleccionar</option>
                  <option value="SIMPLE">Simple</option>
                  <option value="DOBLE">Doble</option>
                  <option value="TRIPLE">Triple</option>
                  <option value="SUITE_FAMILIAR">Suite Familiar</option>
                </select>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Ubicación</label>
                <input name="ubicacion" value={formData.ubicacion} onChange={handleInputChange} style={styles.input} />
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Presupuesto</label>
                <input name="budget" type="number" value={formData.budget} onChange={handleInputChange} style={styles.input} />
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Anotaciones</label>
                <textarea name="annotations" value={formData.annotations} onChange={handleInputChange} style={styles.textarea} />
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Necesidades Especiales</label>
                <textarea name="specialNeeds" value={formData.specialNeeds} onChange={handleInputChange} style={styles.textarea} />
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Comentarios</label>
                <textarea name="comments" value={formData.comments} onChange={handleInputChange} style={styles.textarea} />
              </div>
              <div style={styles.checkboxContainer}><label style={styles.checkboxLabel}><input name="transfer" type="checkbox" checked={formData.transfer} onChange={handleInputChange} style={styles.checkbox} /> Transfer</label></div>
              <div style={styles.checkboxContainer}><label style={styles.checkboxLabel}><input name="carRental" type="checkbox" checked={formData.carRental} onChange={handleInputChange} style={styles.checkbox} /> Alquiler de Auto</label></div>
              <div style={styles.checkboxContainer}><label style={styles.checkboxLabel}><input name="excursion" type="checkbox" checked={formData.excursion} onChange={handleInputChange} style={styles.checkbox} /> Excursión</label></div>
              <div style={styles.checkboxContainer}><label style={styles.checkboxLabel}><input name="traveInsurance" type="checkbox" checked={formData.traveInsurance} onChange={handleInputChange} style={styles.checkbox} /> Seguro de Viaje</label></div>
            </div>
          </div>
          <div style={styles.formButtons}>
            <button type="button" onClick={resetForm} style={styles.cancelButton}>Cancelar</button>
            <button type="submit" style={styles.saveButton}>Guardar</button>
          </div>
        </form>
      )}

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={styles.emptyState}>Cargando solicitudes...</div>
        ) : requests.length === 0 ? (
          <div style={styles.emptyState}>No hay solicitudes registradas.</div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.sortableHeader} onClick={() => handleSort('id')}>ID{getSortArrow('id')}</th>
                  <th style={styles.tableHeader}>Pasajero</th>
                  <th style={styles.tableHeader}>Origen</th>
                  <th style={styles.tableHeader}>Destinos</th>
                  <th style={{...styles.tableHeader, width: '200px'}}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(request => (
                  <tr key={request.id}>
                    <td style={styles.tableCell}>{request.id}</td>
                    <td style={styles.tableCell}>{request.passenger ? `${request.passenger.surname || ''} ${request.passenger.name || ''}` : 'Sin asignar'}</td>
                    <td style={styles.tableCell}>{request.origin || '-'}</td>
                    <td style={styles.tableCell}>
                        {request.requestDestinations?.map(rd => rd.destination ? `${rd.destination.destinationName} - ${rd.destination.country}` : null).filter(Boolean).join(', ') || 'N/A'}
                    </td>
                    <td style={{...styles.tableCell, width: '200px'}}>
                      <div style={styles.actionButtons}>
                        <button onClick={() => handleGenerateBudgetClick(request)} style={styles.generateButton}>Generar Presupuesto</button>
                        <button onClick={() => handleView(request)} style={styles.viewButton}>Ver</button>
                        <button onClick={() => handleEdit(request)} style={styles.editButton}>Editar</button>
                        <button onClick={() => handleDelete(request.id)} style={styles.deleteButton}>Eliminar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.pagination}>
              <div style={styles.paginationInfo}>
                Mostrando {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalElements)} de {totalElements}
              </div>
              <div style={styles.paginationControls}>
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} style={currentPage === 0 ? styles.paginationButtonDisabled : styles.paginationButton}>Anterior</button>
                <span style={styles.pageInfo}>Página {currentPage + 1} de {totalPages}</span>
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} style={currentPage >= totalPages - 1 ? styles.paginationButtonDisabled : styles.paginationButton}>Siguiente</button>
              </div>
            </div>
          </>
        )}
      </div>

      {showViewModal && viewingRequest && (
        <div style={styles.modalOverlay}>
          <div style={showPdfPreview ? styles.pdfViewModal : styles.viewModal}>
            {showPdfPreview ? (
              <>
                <div style={styles.modalHeader}>
                  <h3 style={styles.modalTitle}>{`Solicitud #${viewingRequest.id} - ${viewingRequest.passenger?.surname || 'Cliente'}`}</h3>
                  <button onClick={() => setShowPdfPreview(false)} style={styles.modalCancelButton} type="button">Volver a Detalles</button>
                </div>
                <PDFViewer style={{ width: '100%', height: 'calc(80vh - 100px)' }}>
                  <RequestDocument request={viewingRequest} />
                </PDFViewer>
              </>
            ) : (
              <>
                <div style={styles.modalHeader}>
                  <h3 style={styles.modalTitle}>Información de la Solicitud</h3>
                  <button onClick={() => { setShowViewModal(false); setShowPdfPreview(false); }} style={styles.closeButton} type="button">×</button>
                </div>
                <div style={styles.viewContent}>
                  {viewingRequest.passenger && (
                    <div style={styles.passengerInfoSection}>
                      <h4 style={styles.passengerInfoTitle}>Datos del Pasajero</h4>
                      <div style={styles.viewGrid}>
                        <div style={styles.viewRow}><strong>Apellido:</strong> {viewingRequest.passenger.surname || 'N/A'}</div>
                        <div style={styles.viewRow}><strong>Nombre:</strong> {viewingRequest.passenger.name || 'N/A'}</div>
                        <div style={styles.viewRow}><strong>DNI:</strong> {viewingRequest.passenger.dni || 'N/A'}</div>
                        <div style={styles.viewRow}><strong>Teléfono:</strong> {viewingRequest.passenger.phoneNumber || 'N/A'}</div>
                        <div style={styles.viewRow}><strong>Email:</strong> {viewingRequest.passenger.email || 'N/A'}</div>
                      </div>
                    </div>
                  )}
                  
                  <div style={styles.itinerarySection}>
                      <h4 style={styles.sectionTitle}>Itinerario</h4>
                      {viewingRequest.requestDestinations && viewingRequest.requestDestinations.length > 0 ? (
                          <table style={{...styles.table, ...styles.viewTable}}>
                              <thead>
                                  <tr>
                                      <th style={styles.viewTableHeader}>Destino</th>
                                      <th style={styles.viewTableHeader}>Salida</th>
                                      <th style={styles.viewTableHeader}>Regreso</th>
                                      <th style={styles.viewTableHeader}>Noches</th>
                                  </tr>
                              </thead>
                              <tbody>
                                  {viewingRequest.requestDestinations.map(rd => (
                                      <tr key={rd.id}>
                                          <td style={styles.viewTableCell}>{rd.destination ? `${rd.destination.destinationName} - ${rd.destination.country}` : 'N/A'}</td>
                                          <td style={styles.viewTableCell}>{formatDate(rd.departureDate)}</td>
                                          <td style={styles.viewTableCell}>{formatDate(rd.returnDate)}</td>
                                          <td style={styles.viewTableCell}>{rd.numberNights}</td>
                                      </tr>
                                  ))}
                              </tbody>
                          </table>
                      ) : <p>No hay destinos en el itinerario.</p>}
                  </div>

                  <div style={styles.requestInfoSection}>
                    <h4 style={styles.requestInfoTitle}>Datos Adicionales</h4>
                    <div style={styles.viewGrid}>
                      <div style={styles.viewRow}><strong>ID Solicitud:</strong> {viewingRequest.id}</div>
                      <div style={styles.viewRow}><strong>Origen:</strong> {viewingRequest.origin}</div>
                      <div style={styles.viewRow}><strong>Pasajeros:</strong> {viewingRequest.numberPassengers}</div>
                      <div style={styles.viewRow}><strong>Niños:</strong> {viewingRequest.numberChilds}</div>
                      <div style={styles.viewRow}><strong>Bebés:</strong> {viewingRequest.numberInfants}</div>
                      <div style={styles.viewRow}><strong>Equipaje:</strong> {viewingRequest.luggage || 'N/A'}</div>
                      <div style={styles.viewRow}><strong>Alojamiento:</strong> {accommodationMap[viewingRequest.accommodationPreference] || 'N/A'}</div>
                      <div style={styles.viewRow}><strong>Categoría:</strong> {categoryMap[viewingRequest.category] || 'N/A'}</div>
                      <div style={styles.viewRow}><strong>Plan Comidas:</strong> {mealPlanMap[viewingRequest.mealPlan] || 'N/A'}</div>
                      <div style={styles.viewRow}><strong>Tipo Hab.:</strong> {roomTypeMap[viewingRequest.roomType] || 'N/A'}</div>
                      <div style={styles.viewRow}><strong>Ubicación:</strong> {viewingRequest.ubicacion || 'N/A'}</div>
                      <div style={styles.viewRow}><strong>Presupuesto:</strong> ${viewingRequest.budget}</div>
                      <div style={styles.viewRow}><strong>Transfer:</strong> {viewingRequest.transfer ? 'Sí' : 'No'}</div>
                      <div style={styles.viewRow}><strong>Alq. Auto:</strong> {viewingRequest.carRental ? 'Sí' : 'No'}</div>
                      <div style={styles.viewRow}><strong>Excursión:</strong> {viewingRequest.excursion ? 'Sí' : 'No'}</div>
                      <div style={styles.viewRow}><strong>Seguro Viaje:</strong> {viewingRequest.traveInsurance ? 'Sí' : 'No'}</div>
                    </div>
                  </div>
                  {viewingRequest.annotations && <div style={styles.viewRow}><strong>Anotaciones:</strong> {viewingRequest.annotations}</div>}
                  {viewingRequest.specialNeeds && <div style={styles.viewRow}><strong>Necesidades Especiales:</strong> {viewingRequest.specialNeeds}</div>}
                  {viewingRequest.comments && <div style={styles.viewRow}><strong>Comentarios:</strong> {viewingRequest.comments}</div>}
                </div>
                <div style={styles.modalButtons}>
                  <button onClick={() => setShowPdfPreview(true)} style={styles.pdfButton}>Previsualizar PDF</button>
                  <button onClick={() => { setShowViewModal(false); setShowPdfPreview(false); }} style={styles.modalCancelButton}>Cerrar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Confirmar Eliminación</h3>
            <p style={styles.modalText}>¿Estás seguro? Se eliminará la solicitud y todos sus destinos asociados.</p>
            <div style={styles.modalButtons}>
              <button onClick={cancelDelete} style={styles.modalCancelButton}>Cancelar</button>
              <button onClick={confirmDelete} style={styles.modalConfirmButton}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '5px', maxWidth: '100%', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
  title: { fontSize: '36px', fontWeight: 'bold', color: '#333', margin: 0 },
  addButton: { backgroundColor: '#ff9f43', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '8px', fontSize: '20px', fontWeight: '500', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  form: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', marginBottom: '30px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' },
  formTitle: { fontSize: '28px', fontWeight: '600', marginBottom: '20px', color: '#333' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '25px' },
  fieldContainer: { display: 'flex', flexDirection: 'column' },
  label: { fontSize: '16px', fontWeight: 'bold', color: '#495057', marginBottom: '4px' },
  input: { padding: '16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '18px', backgroundColor: '#fff' },
  select: { padding: '16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '18px', backgroundColor: '#fff' },
  textarea: { padding: '16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '18px', backgroundColor: '#fff', minHeight: '80px', resize: 'vertical' },
  checkboxContainer: { display: 'flex', alignItems: 'center', padding: '12px 0' },
  checkboxLabel: { display: 'flex', alignItems: 'center', fontSize: '18px', color: '#495057', cursor: 'pointer' },
  checkbox: { marginRight: '8px' },
  formButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  saveButton: { backgroundColor: '#28a745', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  cancelButton: { backgroundColor: '#6c757d', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  tableContainer: { backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { backgroundColor: '#f8f9fa', fontWeight: '600', padding: '20px 40px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', width: '180px', minWidth: '180px', fontSize: '18px' },
  sortableHeader: { backgroundColor: '#f8f9fa', fontWeight: '600', padding: '20px 40px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', fontSize: '18px' },
  tableCell: { padding: '20px 40px', borderBottom: '1px solid #dee2e6', fontSize: '18px' },
  actionButtons: { display: 'flex', gap: '8px', whiteSpace: 'nowrap' },
  editButton: { backgroundColor: '#17a2b8', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  deleteButton: { backgroundColor: '#dc3545', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  generateButton: { backgroundColor: '#6f42c1', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  emptyState: { textAlign: 'center', padding: '40px', color: '#6c757d', fontSize: '20px' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #dee2e6', backgroundColor: '#f8f9fa' },
  paginationInfo: { fontSize: '18px', color: '#6c757d' },
  paginationControls: { display: 'flex', alignItems: 'center', gap: '15px' },
  paginationButton: { padding: '12px 24px', backgroundColor: '#ff9f43', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '18px' },
  paginationButtonDisabled: { padding: '12px 24px', backgroundColor: '#e9ecef', color: '#6c757d', border: 'none', borderRadius: '4px', cursor: 'not-allowed', fontSize: '18px' },
  pageInfo: { fontSize: '18px', fontWeight: '500', color: '#495057' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxWidth: '400px', width: '90%' },
  modalTitle: { fontSize: '28px', fontWeight: '600', marginBottom: '15px', color: '#333', textAlign: 'center' },
  modalText: { fontSize: '20px', color: '#495057', marginBottom: '25px', textAlign: 'center', lineHeight: '1.5' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'center' },
  modalCancelButton: { backgroundColor: '#6c757d', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  modalConfirmButton: { backgroundColor: '#dc3545', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  viewButton: { backgroundColor: '#28a745', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  pdfButton: { backgroundColor: '#17a2b8', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer', marginRight: '10px' },
  viewModal: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxWidth: '800px', width: '90%', maxHeight: '80vh', overflowY: 'auto' },
  pdfViewModal: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', width: '90vw', height: '90vh', display: 'flex', flexDirection: 'column', padding: '20px' },
  viewContent: { marginBottom: '25px' },
  viewRow: { padding: '12px 0', borderBottom: '1px solid #e9ecef', fontSize: '20px' },
  viewGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' },
  passengerSection: { marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' },
  requestSection: { marginBottom: '25px' },
  sectionTitle: { fontSize: '20px', fontWeight: '600', color: '#495057', marginBottom: '15px', borderBottom: '2px solid #ff9f43', paddingBottom: '5px' },
  passengerGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' },
  autocompleteContainer: { position: 'relative' },
  suggestionsDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ddd', borderTop: 'none', borderRadius: '0 0 6px 6px', maxHeight: '200px', overflowY: 'auto', zIndex: 1000, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  suggestionItem: { padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f1f1', fontSize: '16px' },
  statusMessage: { backgroundColor: '#d4edda', color: '#155724', padding: '12px', borderRadius: '6px', marginBottom: '15px', border: '1px solid #c3e6cb', fontSize: '16px' },
  statusMessageNew: { backgroundColor: '#fff3cd', color: '#856404', padding: '12px', borderRadius: '6px', marginBottom: '15px', border: '1px solid #ffeaa7', fontSize: '16px' },
  statusMessageError: { backgroundColor: '#f8d7da', color: '#721c24', padding: '12px', borderRadius: '6px', marginBottom: '15px', border: '1px solid #f5c6cb', fontSize: '16px' },
  passengerInfoSection: { marginBottom: '25px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' },
  passengerInfoTitle: { fontSize: '22px', fontWeight: '600', color: '#495057', marginBottom: '10px', borderBottom: '2px solid #ff9f43', paddingBottom: '5px' },
  requestInfoSection: { marginBottom: '15px' },
  requestInfoTitle: { fontSize: '22px', fontWeight: '600', color: '#495057', marginBottom: '10px', borderBottom: '2px solid #17a2b8', paddingBottom: '5px' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  closeButton: { backgroundColor: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6c757d', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' },
  itinerarySection: { marginBottom: '30px', padding: '20px', backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #e0e0e0' },
  viewTable: { fontSize: '18px' },
  viewTableHeader: { backgroundColor: '#e9ecef', padding: '10px', textAlign: 'left' },
  viewTableCell: { padding: '10px', borderBottom: '1px solid #f1f1f1' },
  searchContainer: { backgroundColor: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', gap: '15px', alignItems: 'end', flexWrap: 'wrap' },
  searchField: { display: 'flex', flexDirection: 'column', minWidth: '200px' },
  searchLabel: { fontSize: '14px', fontWeight: '600', color: '#495057', marginBottom: '5px' },
  searchSelect: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', backgroundColor: '#fff' },
  searchInput: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', backgroundColor: '#fff', minWidth: '250px' },
  clearButton: { backgroundColor: '#6c757d', color: 'white', padding: '12px 20px', border: 'none', borderRadius: '6px', fontSize: '16px', cursor: 'pointer', height: 'fit-content' },
}

export default Requests;